import omit from 'lodash/omit';

import { getSessionData } from '@confluence/session-data';

import type { ExperimentalCrossProductSearchFilter } from '../__types__/ExperimentalSchema';
import type { ExperimentalQueries } from '../__types__/ExperimentalQueries';
import { fetchJSON } from '../fetchJSON';
import { MediaType } from '../MediaType';

const XPSEARCH_ADVANCED_SEARCH_API = '/gateway/api/xpsearch-aggregator/search/v2/advanced';
const DEFAULT_SCOPE = 'confluence.page,blogpost,attachment,space';

export type ConfluenceItemContentType = 'page' | 'blogpost' | 'space';

export type Container = {
	title: string;
	id?: string;
	displayUrl?: string;
};

export type ConfItemResponseItem = {
	title: string;
	baseUrl: string;
	url: string;
	content?: {
		id: string;
		type: ConfluenceItemContentType;
		space?: {
			id: string;
		};
	};
	container: Container;
	space?: {
		id?: string;
		key: string;
		icon: {
			path: string;
		};
	};
	entityType: string;
	id?: string;
	iconCssClass: string;
	lastModified: string;
};

const getContainerId = (confItem: ConfItemResponseItem): string | undefined => {
	if (confItem.entityType === 'content') {
		return confItem.container.id;
	}
	if (confItem.entityType === 'space') {
		return confItem.id;
	}
	return undefined;
};

const getContainerKey = (confItem: ConfItemResponseItem): string | undefined => {
	const displayUrl = confItem.container.displayUrl;
	return displayUrl ? displayUrl.split('/spaces/')[1] : '';
};

export const transformSearchResults = ({ content }: { content: ConfItemResponseItem }) => {
	const trimmedContent = omit(content, ['baseUrl', 'container']);
	return {
		...trimmedContent,
		title: content.title,
		content: {
			...trimmedContent.content,
			space: {
				id: getContainerId(content),
				key: getContainerKey(content),
				name: content.container && content.container.title,
				links: {
					webui: content.container && content.container.displayUrl,
				},
			},
		},
	};
};

const mapFilters = (filters: ExperimentalCrossProductSearchFilter | null | undefined) => {
	const filterArray: Array<{ '@type': string; [key: string]: any }> = [];
	if (!filters) {
		return;
	}
	if (filters.spaceKeys) {
		filterArray.push({
			'@type': 'spaces',
			spaceKeys: filters.spaceKeys,
		});
	}
	if (filters.contributors) {
		filterArray.push({
			'@type': 'contributors',
			accountIds: filters.contributors,
		});
	}
	if (filters.date) {
		const { from, to } = filters.date;
		filterArray.push({
			'@type': 'lastModified',
			from,
			to,
		});
	}
	if (filters.labels) {
		filterArray.push({
			'@type': 'labels',
			labels: filters.labels,
		});
	}
	if (filters.titleMatchOnly) {
		filterArray.push({
			'@type': 'titleMatchOnly',
			titleMatchOnly: filters.titleMatchOnly,
		});
	}
	if (filters.ancestorIds) {
		filterArray.push({
			'@type': 'ancestorIds',
			ancestorIds: filters.ancestorIds,
		});
	}
	if (filters.contentStatuses) {
		filterArray.push({
			'@type': 'statuses',
			statuses: filters.contentStatuses,
		});
	}
	if (filters.containerStatuses) {
		filterArray.push({
			'@type': 'containerStatuses',
			containerStatuses: filters.containerStatuses,
		});
	}
	return filterArray;
};

export const experimentalCrossProductAdvancedSearch: ExperimentalQueries['experimentalCrossProductAdvancedSearch'] =
	(_, { query, searchSession, first, after, scope, experimentId, filters }, { network }) =>
		getSessionData()
			.then(({ cloudId }) =>
				fetchJSON(network, XPSEARCH_ADVANCED_SEARCH_API, {
					method: 'POST',
					headers: {
						'Content-Type': MediaType.JSON,
					},
					body: JSON.stringify({
						query,
						cloudId,
						scope: scope || DEFAULT_SCOPE,
						experimentId,
						sessionAttributes: {
							experience: 'confluence.advancedSearch',
							searchSession,
						},
						filters: mapFilters(filters),
						pagination: {
							limit: first,
							token: after,
						},
					}),
				}),
			)
			.then((response) => {
				if (response?.error) {
					throw new Error(`ExperiementalCrossProductSearch error: ${response.error?.message}`);
				}
				const results = response?.results;
				return {
					nodes: (results?.items || []).map(transformSearchResults),
					pageInfo: {
						hasNextPage: !!results?.pagination?.next,
						endCursor: results?.pagination?.next,
						totalCount: results?.totalCount,
					},
					abTest: results?.abTest || {},
				};
			});
